import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import { merge } from 'lodash-es';
import LineChart from '@intelligence/components/Chart/components/LineChart';
import { isEmpty, getElementStyleMathValue } from '@gp/utils/common';
import { getStaticLineOption, getDynamicLineOption } from './utils';

import type { IBaseProps } from '@home/types/common';
import type { IDevBudgetChangeVO } from '@gp/types/finance';

interface IBudgetChangeLineChartProps extends IBaseProps {
  valueFormatter?: (payload: any) => string;
  percentageFormatter?: (payload: any) => string;
  data?: IDevBudgetChangeVO[] | null;
}

const getIsXAxisLabelOverflow = (containerWidth?: number, axisLabelCount?: number): boolean => {
  if (isEmpty(containerWidth) || isEmpty(axisLabelCount)) return false;
  const axisLabelWidth = (axisLabelCount as number) * (100 + 10) - 10;
  return !((containerWidth as number) >= axisLabelWidth);
};

const getOthersLineOption = (isXAxisLabelOverflow: boolean) => {
  const gridBottom = isXAxisLabelOverflow ? -60 : -5;
  const xAxisLabelMargin = isXAxisLabelOverflow ? 54 : 12;
  const xAxisLabelRotate = isXAxisLabelOverflow ? 45 : 0;
  return {
    grid: {
      bottom: gridBottom,
    },
    xAxis: {
      axisLabel: {
        margin: xAxisLabelMargin,
        rotate: xAxisLabelRotate,
      },
    },
  };
};

const BudgetChangeLineChart: React.FC<IBudgetChangeLineChartProps> = (props) => {
  const { style, className, valueFormatter, percentageFormatter, data: list } = props;

  const chartContainer = useRef<HTMLDivElement>(null);

  const [lineOption, setLineOption] = useState<IEChartOption>(() => getStaticLineOption({ valueFormatter }));

  const getOthersLineOptionInPage = (list?: IDevBudgetChangeVO[] | null) => {
    if (!chartContainer.current) return {};
    const containerWidth = getElementStyleMathValue(chartContainer.current, 'width');
    const isXAxisLabelOverflow = getIsXAxisLabelOverflow(containerWidth, list?.length);
    return getOthersLineOption(isXAxisLabelOverflow);
  };

  const handleResize = () => {
    if (!chartContainer.current) return;
    const othersLineOption = getOthersLineOptionInPage(list);
    const lineOptionTmp = merge({}, lineOption, othersLineOption);
    setLineOption(lineOptionTmp);
  };

  useEffect(() => {
    if (!chartContainer.current) return;
    if (!Array.isArray(list) || !list.length) return;
    const dynamicLineOption = getDynamicLineOption(list, { valueFormatter, percentageFormatter });
    const othersLineOption = getOthersLineOptionInPage(list);
    const lineOptionTmp = merge({}, lineOption, dynamicLineOption, othersLineOption);
    setLineOption(lineOptionTmp);
  }, [list]);

  useEffect(() => {
    window.addEventListener('resize', handleResize, false);

    return () => {
      window.removeEventListener('resize', handleResize, false);
    };
  }, [list, lineOption]);

  return (
    <div
      ref={chartContainer}
      style={style}
      className={classnames('dbg-project-details-finance-dev-budget-change-linechart-container', className)}
    >
      <LineChart
        className="dbg-project-details-finance-dev-budget-change-linechart"
        height={350}
        lineOption={lineOption}
      />
    </div>
  );
};

export default BudgetChangeLineChart;
